/**
 * UserTable — shows all users with their current seat type.
 * Seat changes go through onAssignSeat; removal through onRemoveSeat.
 *
 * Props:
 *   users      — [{ id, clerk_user_id, userName, userEmail, seat_types: {id, slug, name} }]
 *   seatTypes  — [{ id, slug, name }]
 *   dark       — boolean
 *   onAssignSeat(clerkUserId, seatTypeId) — called on seat change
 *   onRemoveSeat(clerkUserId)             — called on remove
 *   onSetBqeCoreId(user, value) -> Promise — saves the member's BQE Core resource id
 *   onSetEmployment(user, 'employee'|'contractor') -> Promise — changes their employment type
 *   onSetPosition(user, positionId) -> Promise<{ billRate }> — sets their Practice Position
 *   positions  — [{ id, name, rate }] Practice Positions (each carries a bill rate)
 *   modules    — [{ id, slug, name }] every module in the app
 *   grants     — [{ clerkUserId, moduleSlug }] module access given to a PERSON, on top of a seat
 *   onGrant(clerkUserId, moduleSlug) -> Promise
 *   onRevoke(clerkUserId, moduleSlug) -> Promise
 */

import { useState, useReducer } from 'react'
import Select from '../Select.jsx'

// Editable BQE Core id cell. Saves on blur / Enter, only when the value changed. Disabled until the
// user has an Airtable Member (memberId) to write to.
function BqeCell({ u, inputBg, inputBdr, textPrim, textMuted, onSetBqeCoreId }) {
  const [val, setVal] = useState(u.bqeContactId || '')
  const [state, setState] = useState('')   // '' | 'saving' | 'saved' | 'error'
  const disabled = !u.memberId
  function save() {
    const next = (val || '').trim()
    if (next === (u.bqeContactId || '')) return
    setState('saving')
    Promise.resolve(onSetBqeCoreId(u, next))
      .then(() => { u.bqeContactId = next; setState('saved') })
      .catch(() => setState('error'))
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <input
        value={val}
        disabled={disabled}
        onChange={e => { setVal(e.target.value); if (state) setState('') }}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
        placeholder={disabled ? 'no member yet' : 'Core resource id'}
        title={disabled ? 'This user has no Airtable Member record yet.' : 'BQE Core resource id — enables posting their time to Core'}
        style={{
          fontFamily: '"DM Mono", monospace', fontSize: 11, color: textPrim,
          background: inputBg, border: `0.5px solid ${inputBdr}`, borderRadius: 4,
          padding: '4px 8px', width: '100%', outline: 'none', boxSizing: 'border-box',
          opacity: disabled ? 0.5 : 1,
        }}
      />
      {state === 'saving' && <span style={{ fontSize: 10, color: textMuted }}>Saving…</span>}
      {state === 'saved'  && <span style={{ fontSize: 10, color: '#1D9E75' }}>Saved</span>}
      {state === 'error'  && <span style={{ fontSize: 10, color: '#c0392b' }}>Save failed</span>}
    </div>
  )
}

// Employment type (employee / contractor). Full-time employees get the time card hours minimum;
// contractors confirm their time each day instead. Same setting as Admin → Time → Member policy.
function EmploymentCell({ u, dark, textMuted, onSetEmployment }) {
  const [val, setVal] = useState(u.employmentType || '')
  const [state, setState] = useState('')   // '' | 'saving' | 'saved' | 'error'
  const disabled = !u.memberId
  function change(next) {
    if (!next || next === val) return
    const prev = val
    setVal(next); setState('saving')
    Promise.resolve(onSetEmployment(u, next))
      .then(() => { u.employmentType = next; setState('saved') })
      .catch(() => { setVal(prev); setState('error') })
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }} title={disabled ? 'This user has no Airtable Member record yet.' : undefined}>
      <Select
        value={val}
        onChange={change}
        options={[{ value: 'employee', label: 'Employee' }, { value: 'contractor', label: 'Contractor' }]}
        placeholder={disabled ? 'no member yet' : 'not set'}
        dark={dark}
        disabled={disabled}
        style={{ fontSize: 12, padding: '4px 8px', width: '100%', opacity: disabled ? 0.5 : 1 }}
      />
      {state === 'saving' && <span style={{ fontSize: 10, color: textMuted }}>Saving…</span>}
      {state === 'saved'  && <span style={{ fontSize: 10, color: '#1D9E75' }}>Saved</span>}
      {state === 'error'  && <span style={{ fontSize: 10, color: '#c0392b' }}>Save failed</span>}
    </div>
  )
}

const money = (n) => '$' + Number(n).toFixed(2)

/**
 * Practice Position, and the bill rate it gives this person. The rate is what every time entry they
 * log is valued at (copied onto the entry when it is created), so a blank one means their hours count
 * as $0 in phase spend, EBR and budget alerts until it is fixed. Nothing in the invite sets this,
 * which is how two people went a month without one (2026-10). Everyone bills the position's
 * rate; there is no separate contractor rate any more (Matt, 2026-10-14).
 */
function PositionCell({ u, positions, dark, textMuted, onSetPosition, onRateChange }) {
  const [val, setVal] = useState(u.positionId || '')
  const [state, setState] = useState('')   // '' | 'saving' | 'error'
  const disabled = !u.userEmail || !u.billRateKnown
  function change(next) {
    if (!next || next === val) return
    const prev = val
    setVal(next); setState('saving')
    Promise.resolve(onSetPosition(u, next))
      .then(r => {
        u.positionId = next
        u.billRate = r && r.billRate !== undefined ? r.billRate : u.billRate
        setState(''); onRateChange && onRateChange()
      })
      .catch(() => { setVal(prev); setState('error') })
  }
  const missing = u.billRateKnown && u.billRate == null
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Select
        value={val}
        onChange={change}
        options={positions.map(p => ({ value: p.id, label: p.rate != null ? p.name + ' · ' + money(p.rate) : p.name }))}
        placeholder={!u.billRateKnown ? 'unavailable' : 'not set'}
        dark={dark}
        disabled={disabled}
        style={{
          fontSize: 12, padding: '4px 8px', width: '100%', opacity: disabled ? 0.5 : 1,
          ...(missing ? { borderColor: '#c0392b' } : {}),
        }}
      />
      {state === 'saving' && <span style={{ fontSize: 10, color: textMuted }}>Saving…</span>}
      {state === 'error'  && <span style={{ fontSize: 10, color: '#c0392b' }}>Save failed</span>}
      {!state && missing && (
        <span
          title={u.contactId
            ? 'Their hours are being valued at $0. Pick a position to give them a rate. Entries already logged keep $0 until corrected.'
            : 'No Contact record matches this email, so there is nowhere to set a position.'}
          style={{ fontFamily: '"DM Mono", monospace', fontSize: 10, color: '#c0392b', fontWeight: 600 }}
        >
          {u.contactId ? 'No bill rate' : 'No bill rate · no Contact'}
        </span>
      )}
      {!state && !missing && u.billRate != null && (
        <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 10, color: textMuted }}>
          {money(u.billRate)}/hr
        </span>
      )}
    </div>
  )
}

/**
 * Module access given to this PERSON, on top of whatever their seat already carries.
 *
 * This is where Bookkeeping lives. It was moved off the admin seat onto one named person because
 * it shows payroll, owner draws and distributions, and until now the only way to change that was
 * a hand-written SQL statement. Grants a seat already covers are not offered — a duplicate grant
 * would look like it was doing something and would in fact do nothing, so removing it later would
 * not take the access away.
 */
function AccessCell({ u, modules, grants, seatModuleSlugs, textMuted, dark, onGrant, onRevoke }) {
  const [busy, setBusy] = useState('')
  const mine = grants.filter(g => g.clerkUserId === u.clerk_user_id).map(g => g.moduleSlug)
  const nameOf = (slug) => (modules.find(m => m.slug === slug)?.name) || slug

  // Offer only what their seat does not already give them, and not what they already hold.
  const offerable = modules
    .filter(m => !seatModuleSlugs.includes(m.slug) && !mine.includes(m.slug))
    .sort((a, b) => (a.name || a.slug).localeCompare(b.name || b.slug))

  async function run(fn, key) {
    setBusy(key)
    try { await fn() } finally { setBusy('') }
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
      {mine.map(slug => (
        <span
          key={slug}
          title={`Granted to ${u.userName || 'this person'} by name, not by seat. Click × to revoke.`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontFamily: '"DM Mono", monospace', fontSize: 10,
            color: dark ? 'rgba(207,226,232,0.92)' : '#07272D',
            background: dark ? 'rgba(255,255,255,0.08)' : 'rgba(7,39,45,0.06)',
            border: `0.5px solid ${dark ? 'rgba(255,255,255,0.14)' : 'rgba(7,39,45,0.15)'}`,
            borderRadius: 20, padding: '2px 4px 2px 8px',
            opacity: busy === slug ? 0.4 : 1,
          }}
        >
          {nameOf(slug)}
          <button
            onClick={() => run(() => onRevoke(u.clerk_user_id, slug), slug)}
            disabled={!!busy}
            title="Revoke"
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '0 3px',
              color: 'rgba(192,57,43,0.8)', fontSize: 12, lineHeight: 1,
            }}
          >×</button>
        </span>
      ))}

      {offerable.length > 0 && (
        <Select
          value=""
          onChange={v => { if (v) run(() => onGrant(u.clerk_user_id, v), v) }}
          options={offerable.map(m => ({ value: m.slug, label: m.name || m.slug }))}
          placeholder={busy ? 'Saving…' : '+ add'}
          dark={dark}
          style={{ fontSize: 11, padding: '3px 6px', width: 108 }}
        />
      )}

      {!mine.length && !offerable.length && (
        <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 10, color: textMuted }}>
          seat covers all
        </span>
      )}
    </div>
  )
}

export default function UserTable({ users, seatTypes, dark, onAssignSeat, onRemoveSeat, onSetBqeCoreId, onSetEmployment, onSetPosition, positions = [], modules = [], grants = [], onGrant, onRevoke }) {
  // Cells mutate the row object after a save (the existing pattern here); this re-renders the
  // table so the count above it and the rate under Position follow.
  const [, rerender] = useReducer(x => x + 1, 0)
  const textPrim  = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMuted = dark ? 'rgba(91,132,148,0.80)'  : 'rgba(7,39,45,0.50)'
  const rowBorder = dark ? 'rgba(255,255,255,0.06)' : 'rgba(7,39,45,0.07)'
  const inputBg   = dark ? 'rgba(255,255,255,0.05)' : '#f4f7f8'
  const inputBdr  = dark ? 'rgba(255,255,255,0.10)' : 'rgba(7,39,45,0.15)'

  if (!users.length) {
    return (
      <div style={{ padding: '20px 20px', fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: textMuted }}>
        No active users yet. Send an invite to add team members.
      </div>
    )
  }

  const noRate = users.filter(u => u.billRateKnown && u.billRate == null)

  return (
    <div>
      {noRate.length > 0 && (
        <div style={{
          margin: '12px 20px 4px', padding: '10px 12px', borderRadius: 4,
          border: '0.5px solid rgba(192,57,43,0.45)', background: dark ? 'rgba(192,57,43,0.12)' : 'rgba(192,57,43,0.06)',
          fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: textPrim, lineHeight: 1.45,
        }}>
          <strong style={{ color: '#c0392b' }}>
            {noRate.length === 1 ? '1 person has' : noRate.length + ' people have'} no bill rate:
          </strong>{' '}
          {noRate.map(u => u.userName || u.userEmail).join(', ')}. Every hour they log is valued at $0
          until a Position is set below.
        </div>
      )}
      {/* Column header */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1.1fr 132px 120px 150px 140px 1fr 70px',
        padding: '8px 20px',
        borderBottom: `0.5px solid ${rowBorder}`,
      }}>
        {['User', 'Seat Type', 'Employment', 'Position', 'BQE Core ID', 'Extra Access', ''].map((h, i) => (
          <div key={i} style={{
            fontFamily: '"DM Mono", monospace', fontSize: 10,
            color: textMuted, letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>
            {h}
          </div>
        ))}
      </div>

      {users.map(u => (
        <div
          key={u.id}
          style={{
            display: 'grid', gridTemplateColumns: '1.1fr 132px 120px 150px 140px 1fr 70px',
            alignItems: 'center', padding: '10px 20px', gap: 8,
            borderBottom: `0.5px solid ${rowBorder}`,
          }}
        >
          {/* Name + email */}
          <div style={{ overflow: 'hidden', minWidth: 0 }}>
            <div style={{
              fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 500,
              color: textPrim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {u.userName || u.clerk_user_id}
            </div>
            {u.userEmail && (
              <div style={{
                fontFamily: '"DM Mono", monospace', fontSize: 11,
                color: textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                marginTop: 1,
              }}>
                {u.userEmail}
              </div>
            )}
          </div>

          {/* Seat type selector */}
          <Select
            value={u.seat_types?.id || ''}
            onChange={v => onAssignSeat(u.clerk_user_id, v)}
            options={seatTypes.map(st => ({ value: st.id, label: st.name }))}
            placeholder="— select —"
            dark={dark}
            style={{ fontSize: 12, padding: '4px 8px', width: '100%' }}
          />

          {/* Employment type (time card rules) */}
          <EmploymentCell u={u} dark={dark} textMuted={textMuted} onSetEmployment={onSetEmployment} />

          {/* Practice Position — gives them their bill rate (writes the Airtable Contact) */}
          <PositionCell u={u} positions={positions} dark={dark} textMuted={textMuted} onSetPosition={onSetPosition} onRateChange={rerender} />

          {/* BQE Core resource id (writes the Airtable Member) */}
          <BqeCell u={u} inputBg={inputBg} inputBdr={inputBdr} textPrim={textPrim} textMuted={textMuted} onSetBqeCoreId={onSetBqeCoreId} />

          {/* Module access granted to this person by name, on top of their seat */}
          <AccessCell
            u={u}
            modules={modules}
            grants={grants}
            seatModuleSlugs={(seatTypes.find(st => st.id === u.seat_types?.id)?.modules || []).map(m => m.slug)}
            textMuted={textMuted}
            dark={dark}
            onGrant={onGrant}
            onRevoke={onRevoke}
          />

          {/* Remove */}
          <div style={{ textAlign: 'right' }}>
            <button
              onClick={() => onRemoveSeat(u.clerk_user_id)}
              style={{
                fontFamily: '"DM Sans", sans-serif', fontSize: 12,
                color: 'rgba(192,57,43,0.75)', background: 'none',
                border: 'none', cursor: 'pointer', padding: '2px 4px',
              }}
              title="Remove seat"
            >
              Remove
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}
